import { useMemo, useState } from 'react'
import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { AlertTriangle, BookOpen, CheckCircle2, ClipboardList, FilePenLine, RefreshCw } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getFacilities, getFacilityFeatureFlags } from '../../lib/services/facilities'
import {
  acknowledgePolicy,
  createPolicy,
  getPolicies,
  getPolicy,
  getPolicyAcknowledgmentSummary,
  updatePolicy,
  type PolicyCategory,
  type PolicyInput,
  type PolicyRecord,
  type PolicyStatus,
} from '../../lib/services/policies'

const CATEGORY_LABELS: Record<PolicyCategory, string> = {
  governance: 'Governance',
  patient_rights: 'Patient Rights',
  infection_control: 'Infection Control',
  medication_safety: 'Medication Safety',
  life_safety: 'Life Safety',
  environment_of_care: 'Environment Of Care',
  emergency_preparedness: 'Emergency Preparedness',
  medical_records: 'Medical Records',
  staffing: 'Staffing',
  quality_assessment: 'Quality Assessment',
}

const STATUS_STYLES: Record<PolicyStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  in_review: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  retired: 'bg-rose-50 text-rose-700',
}

function isPastDue(value: string | null): boolean {
  if (!value) return false
  const parsed = new Date(value)
  if (Number.isNaN(parsed.valueOf())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return parsed < today
}

type PolicyDraft = {
  facility_id: string
  title: string
  category: PolicyCategory
  status: PolicyStatus
  summary: string
  content_text: string
  effective_date: string
  review_date: string
}

const DEFAULT_DRAFT: PolicyDraft = {
  facility_id: '',
  title: '',
  category: 'governance',
  status: 'draft',
  summary: '',
  content_text: '',
  effective_date: '',
  review_date: '',
}

function formatDate(value: string | null): string {
  if (!value) return 'Not scheduled'
  const parsed = new Date(value)
  return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleDateString()
}

function toDraft(policy: PolicyRecord | null): PolicyDraft {
  if (!policy) return DEFAULT_DRAFT
  return {
    facility_id: policy.facility_id,
    title: policy.title,
    category: policy.category,
    status: policy.status,
    summary: policy.summary ?? '',
    content_text: policy.content_text ?? '',
    effective_date: policy.effective_date ?? '',
    review_date: policy.review_date ?? '',
  }
}

function toPayload(draft: PolicyDraft): PolicyInput {
  return {
    facility_id: draft.facility_id,
    title: draft.title.trim(),
    category: draft.category,
    status: draft.status,
    summary: draft.summary.trim() || null,
    content_text: draft.content_text.trim() || null,
    effective_date: draft.effective_date || null,
    review_date: draft.review_date || null,
  }
}

export function PolicyLibraryPage() {
  const { viewerContext } = useAuth()
  const [selectedFacility, setSelectedFacility] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<'all' | PolicyCategory>('all')
  const [selectedStatus, setSelectedStatus] = useState<'all' | PolicyStatus>('all')
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null)
  const [draft, setDraft] = useState<PolicyDraft>(DEFAULT_DRAFT)
  const [draftSourcePolicyId, setDraftSourcePolicyId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isAdmin = viewerContext?.role === 'Admin'
  const featureFlagsQuery = useQuery({
    queryKey: ['policy-feature-flags'],
    queryFn: () => getFacilityFeatureFlags(),
  })
  const facilitiesQuery = useQuery({
    queryKey: ['policy-facilities'],
    queryFn: getFacilities,
  })
  const policiesQuery = useQuery({
    queryKey: ['policies', selectedFacility, selectedCategory, selectedStatus],
    queryFn: () => getPolicies({
      facilityId: selectedFacility || undefined,
      category: selectedCategory === 'all' ? undefined : selectedCategory,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
    }),
  })
  const policies = useMemo(() => policiesQuery.data ?? [], [policiesQuery.data])
  const activePolicyId = selectedPolicyId ?? policies[0]?.policy_id ?? null
  const selectedPolicyQuery = useQuery({
    queryKey: ['policy-detail', activePolicyId],
    queryFn: () => getPolicy(activePolicyId as string),
    enabled: activePolicyId !== null,
  })
  const acknowledgmentSummaryQuery = useQuery({
    queryKey: ['policy-ack-summary', activePolicyId],
    queryFn: () => getPolicyAcknowledgmentSummary(activePolicyId as string),
    enabled: activePolicyId !== null && isAdmin,
  })
  const approvedPolicies = useMemo(
    () => policies.filter((policy) => policy.status === 'approved'),
    [policies],
  )
  const approvedPolicyAcknowledgmentQueries = useQueries({
    queries: isAdmin
      ? approvedPolicies.map((policy) => ({
          queryKey: ['policy-ack-summary-card', policy.policy_id],
          queryFn: () => getPolicyAcknowledgmentSummary(policy.policy_id),
        }))
      : [],
  })

  const createMutation = useMutation({
    mutationFn: createPolicy,
    onSuccess: async (policy) => {
      setIsCreating(false)
      setSelectedPolicyId(policy.policy_id)
      setMessage(`Created policy ${policy.title}.`)
      setError(null)
      await Promise.all([policiesQuery.refetch(), selectedPolicyQuery.refetch()])
    },
  })
  const updateMutation = useMutation({
    mutationFn: async (payload: { policyId: string; input: Partial<PolicyInput> }) =>
      await updatePolicy(payload.policyId, payload.input),
    onSuccess: async (policy) => {
      setMessage(`Updated policy ${policy.title}.`)
      setError(null)
      await Promise.all([policiesQuery.refetch(), selectedPolicyQuery.refetch(), acknowledgmentSummaryQuery.refetch()])
    },
  })
  const acknowledgeMutation = useMutation({
    mutationFn: acknowledgePolicy,
    onSuccess: async (acknowledgment) => {
      setMessage(`Acknowledged policy on ${formatDate(acknowledgment.acknowledged_at)}.`)
      setError(null)
      await Promise.all([
        selectedPolicyQuery.refetch(),
        acknowledgmentSummaryQuery.refetch(),
      ])
    },
  })

  const policyModuleEnabled = useMemo(
    () => (featureFlagsQuery.data ?? []).some((flag) => flag.feature_key === 'policy_management' && flag.enabled),
    [featureFlagsQuery.data],
  )
  const selectedPolicy = selectedPolicyQuery.data?.policy ?? null
  const createDraft = isCreating
    ? {
        ...draft,
        facility_id: draft.facility_id || selectedFacility || facilitiesQuery.data?.[0]?.facility_id || '',
      }
    : null
  const activeDraft =
    createDraft ??
    (draftSourcePolicyId === activePolicyId
      ? draft
      : toDraft(selectedPolicy))
  const coverageRows = useMemo(
    () =>
      approvedPolicies.map((policy, index) => {
        const summary = approvedPolicyAcknowledgmentQueries[index]?.data
        const assigned = summary?.assigned_user_count ?? 0
        const acknowledged = summary?.acknowledged_user_count ?? 0
        const coveragePercent = assigned > 0 ? Math.round((acknowledged / assigned) * 100) : 0
        return {
          policy,
          assigned,
          acknowledged,
          outstanding: Math.max(assigned - acknowledged, 0),
          coveragePercent,
        }
      }),
    [approvedPolicies, approvedPolicyAcknowledgmentQueries],
  )
  const totalAssigned = coverageRows.reduce((sum, row) => sum + row.assigned, 0)
  const totalAcknowledged = coverageRows.reduce((sum, row) => sum + row.acknowledged, 0)
  const overallCoveragePercent = totalAssigned > 0 ? Math.round((totalAcknowledged / totalAssigned) * 100) : 0
  const missingApprovedCategories = Object.entries(CATEGORY_LABELS)
    .filter(([category]) => !approvedPolicies.some((policy) => policy.category === category))
    .map(([, label]) => label)
  const expiredReviewPolicies = approvedPolicies.filter((policy) => isPastDue(policy.review_date))
  const outstandingPolicies = coverageRows.filter((row) => row.outstanding > 0)

  if (!policyModuleEnabled && featureFlagsQuery.isSuccess) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Policy management is not enabled for this facility.
      </div>
    )
  }

  const selectedFacilityName =
    facilitiesQuery.data?.find((facility) => facility.facility_id === (selectedPolicy?.facility_id ?? activeDraft.facility_id))?.facility_name ?? 'Selected facility'

  async function handleSave() {
    setMessage(null)
    setError(null)
    try {
      if (isCreating) {
        await createMutation.mutateAsync(toPayload(activeDraft))
        return
      }
      if (!activePolicyId) return
      await updateMutation.mutateAsync({
        policyId: activePolicyId,
        input: toPayload(activeDraft),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save policy')
    }
  }

  async function handleApprovalStatus(nextStatus: PolicyStatus) {
    if (!activePolicyId || !isAdmin) return
    setMessage(null)
    setError(null)
    try {
      await updateMutation.mutateAsync({
        policyId: activePolicyId,
        input: { status: nextStatus },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update approval status')
    }
  }

  async function handleAcknowledge() {
    if (!activePolicyId || isAdmin) return
    setMessage(null)
    setError(null)
    try {
      await acknowledgeMutation.mutateAsync(activePolicyId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge policy')
    }
  }

  return (
    <div className="space-y-6">
      <header className="v11-workspace-header">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Policies</h1>
            <p className="mt-1 text-sm text-slate-600">
              Module-gated policy workspace built on the Sprint 1 backend foundation. Sprint 3 adds bounded acknowledgments plus coverage and gap analysis without reopening export or notification scope.
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <button
                className="btn-primary px-4 py-2 text-sm"
                onClick={() => {
                  setIsCreating(true)
                  setSelectedPolicyId(null)
                  setDraftSourcePolicyId(null)
                  setDraft({
                    ...DEFAULT_DRAFT,
                    facility_id: selectedFacility || facilitiesQuery.data?.[0]?.facility_id || '',
                  })
                }}
              >
                New Policy
              </button>
            )}
            <button
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={() => void policiesQuery.refetch()}
            >
              <RefreshCw size={16} className="mr-2 inline" />
              Refresh
            </button>
          </div>
        </div>
        {message && <p className="mt-3 text-sm text-emerald-700">{message}</p>}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </header>

      <div className="grid gap-6 xl:grid-cols-[1.05fr,1.4fr]">
        <section className="v11-section p-5">
          <div className="flex flex-col gap-3 md:flex-row md:flex-wrap">
            {facilitiesQuery.data && facilitiesQuery.data.length > 1 && (
              <select
                value={selectedFacility}
                onChange={(event) => setSelectedFacility(event.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
              >
                <option value="">All facilities</option>
                {facilitiesQuery.data.map((facility) => (
                  <option key={facility.facility_id} value={facility.facility_id}>
                    {facility.facility_name}
                  </option>
                ))}
              </select>
            )}
            <select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value as 'all' | PolicyCategory)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
            >
              <option value="all">All categories</option>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value as 'all' | PolicyStatus)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="in_review">In review</option>
              <option value="approved">Approved</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          <div className="mt-4 space-y-3">
            {policiesQuery.isLoading && <p className="text-sm text-slate-500">Loading policies...</p>}
            {!policiesQuery.isLoading && policies.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 p-5 text-sm text-slate-500">
                No policies match the current filters.
              </div>
            )}
            {policies.map((policy) => (
              <button
                key={policy.policy_id}
                className={`w-full rounded-2xl border p-4 text-left transition ${
                  activePolicyId === policy.policy_id
                    ? 'border-[var(--brand-primary)] bg-blue-50'
                    : 'border-slate-200 bg-white hover:border-[var(--brand-primary)] hover:shadow-sm'
                }`}
                onClick={() => {
                  setIsCreating(false)
                  setSelectedPolicyId(policy.policy_id)
                  setDraftSourcePolicyId(null)
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold text-slate-900">{policy.title}</div>
                    <div className="mt-1 text-xs font-semibold uppercase tracking-widest text-slate-500">
                      {CATEGORY_LABELS[policy.category]} · v{policy.version_number}
                    </div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[policy.status]}`}>
                    {policy.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="mt-3 text-sm text-slate-600">{policy.summary || 'No summary yet.'}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                  <span>Review: {formatDate(policy.review_date)}</span>
                  <span>Effective: {formatDate(policy.effective_date)}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="v11-section p-5">
          {isCreating || selectedPolicy ? (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-slate-500">
                    <BookOpen size={14} />
                    {isCreating ? 'New policy draft' : 'Policy detail'}
                  </div>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">
                    {isCreating ? 'Create a new policy' : selectedPolicy?.title}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">{selectedFacilityName}</p>
                </div>
                {!isCreating && selectedPolicy && (
                  <span className={`rounded-full px-3 py-1 text-sm font-bold ${STATUS_STYLES[selectedPolicy.status]}`}>
                    {selectedPolicy.status.replace('_', ' ')}
                  </span>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold text-slate-700">
                  Title
                  <input
                    value={activeDraft.title}
                    onChange={(event) => {
                      setDraftSourcePolicyId(activePolicyId)
                      setDraft({ ...activeDraft, title: event.target.value })
                    }}
                    disabled={!isAdmin}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Facility
                  <select
                    value={activeDraft.facility_id}
                    onChange={(event) => {
                      setDraftSourcePolicyId(activePolicyId)
                      setDraft({ ...activeDraft, facility_id: event.target.value })
                    }}
                    disabled={!isAdmin}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Select facility</option>
                    {(facilitiesQuery.data ?? []).map((facility) => (
                      <option key={facility.facility_id} value={facility.facility_id}>
                        {facility.facility_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Category
                  <select
                    value={activeDraft.category}
                    onChange={(event) => {
                      setDraftSourcePolicyId(activePolicyId)
                      setDraft({ ...activeDraft, category: event.target.value as PolicyCategory })
                    }}
                    disabled={!isAdmin}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Status
                  <select
                    value={activeDraft.status}
                    onChange={(event) => {
                      setDraftSourcePolicyId(activePolicyId)
                      setDraft({ ...activeDraft, status: event.target.value as PolicyStatus })
                    }}
                    disabled={!isAdmin}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="draft">Draft</option>
                    <option value="in_review">In review</option>
                    <option value="approved">Approved</option>
                    <option value="retired">Retired</option>
                  </select>
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Effective date
                  <input
                    type="date"
                    value={activeDraft.effective_date}
                    onChange={(event) => {
                      setDraftSourcePolicyId(activePolicyId)
                      setDraft({ ...activeDraft, effective_date: event.target.value })
                    }}
                    disabled={!isAdmin}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Review date
                  <input
                    type="date"
                    value={activeDraft.review_date}
                    onChange={(event) => {
                      setDraftSourcePolicyId(activePolicyId)
                      setDraft({ ...activeDraft, review_date: event.target.value })
                    }}
                    disabled={!isAdmin}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
              </div>

              <label className="block text-sm font-semibold text-slate-700">
                Summary
                <textarea
                  value={activeDraft.summary}
                  onChange={(event) => {
                    setDraftSourcePolicyId(activePolicyId)
                    setDraft({ ...activeDraft, summary: event.target.value })
                  }}
                  disabled={!isAdmin}
                  rows={3}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
              </label>

              <label className="block text-sm font-semibold text-slate-700">
                Policy content
                <textarea
                  value={activeDraft.content_text}
                  onChange={(event) => {
                    setDraftSourcePolicyId(activePolicyId)
                    setDraft({ ...activeDraft, content_text: event.target.value })
                  }}
                  disabled={!isAdmin}
                  rows={10}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
              </label>

              {!isCreating && selectedPolicy && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="v11-mini-metric text-left">
                    <div className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-slate-500">
                      <ClipboardList size={14} />
                      Detail
                    </div>
                    <dl className="mt-3 space-y-2 text-sm text-slate-600">
                      <div className="flex justify-between gap-3">
                        <dt>Version</dt>
                        <dd className="font-semibold text-slate-900">v{selectedPolicy.version_number}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt>CMS sections</dt>
                        <dd className="font-semibold text-slate-900">{selectedPolicy.cms_cfc_sections.join(', ') || 'None'}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt>Approved at</dt>
                        <dd className="font-semibold text-slate-900">{formatDate(selectedPolicy.approved_at)}</dd>
                      </div>
                    </dl>
                  </div>
                  <div className="v11-mini-metric text-left">
                    <div className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-slate-500">
                      <CheckCircle2 size={14} />
                      Acknowledgment summary
                    </div>
                    <dl className="mt-3 space-y-2 text-sm text-slate-600">
                      <div className="flex justify-between gap-3">
                        <dt>Current acknowledgments</dt>
                        <dd className="font-semibold text-slate-900">{selectedPolicyQuery.data?.acknowledgmentSummary.total_acknowledgments ?? 0}</dd>
                      </div>
                      {isAdmin && acknowledgmentSummaryQuery.data && (
                        <>
                          <div className="flex justify-between gap-3">
                            <dt>Assigned users</dt>
                            <dd className="font-semibold text-slate-900">{acknowledgmentSummaryQuery.data.assigned_user_count}</dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt>Acknowledged users</dt>
                            <dd className="font-semibold text-slate-900">{acknowledgmentSummaryQuery.data.acknowledged_user_count}</dd>
                          </div>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="v11-kpi-card">
                    <div className="text-xs font-black uppercase tracking-widest text-slate-500">Coverage</div>
                    <div className="mt-2 text-3xl font-bold text-slate-900">{overallCoveragePercent}%</div>
                    <p className="mt-2 text-sm text-slate-600">
                      {totalAcknowledged} of {totalAssigned} assigned acknowledgments completed across approved policies.
                    </p>
                  </div>
                  <div className="v11-kpi-card v11-kpi-accent-gold">
                    <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-amber-700">
                      <AlertTriangle size={14} />
                      Gaps
                    </div>
                    <div className="mt-2 text-3xl font-bold text-amber-900">
                      {missingApprovedCategories.length + expiredReviewPolicies.length + outstandingPolicies.length}
                    </div>
                    <p className="mt-2 text-sm text-amber-800">
                      Missing approved categories, expired reviews, and incomplete acknowledgments.
                    </p>
                  </div>
                  <div className="v11-kpi-card v11-kpi-accent-teal">
                    <div className="text-xs font-black uppercase tracking-widest text-slate-500">Outstanding acknowledgments</div>
                    <div className="mt-2 text-3xl font-bold text-slate-900">
                      {outstandingPolicies.reduce((sum, row) => sum + row.outstanding, 0)}
                    </div>
                    <p className="mt-2 text-sm text-slate-600">
                      Remaining confirmations needed before full facility coverage.
                    </p>
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="v11-mini-metric text-left">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">Missing approved categories</h3>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700">
                      {missingApprovedCategories.length > 0 ? missingApprovedCategories.map((label) => (
                        <li key={label}>{label}</li>
                      )) : <li>None. Every governed category has an approved policy.</li>}
                    </ul>
                  </div>
                  <div className="v11-mini-metric text-left">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">Expired review dates</h3>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700">
                      {expiredReviewPolicies.length > 0 ? expiredReviewPolicies.map((policy) => (
                        <li key={policy.policy_id}>{policy.title} · Review date {formatDate(policy.review_date)}</li>
                      )) : <li>None. Approved policies are within review dates.</li>}
                    </ul>
                  </div>
                  <div className="v11-mini-metric text-left">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">Outstanding acknowledgments</h3>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700">
                      {outstandingPolicies.length > 0 ? outstandingPolicies.map((row) => (
                        <li key={row.policy.policy_id}>
                          {row.policy.title} · {row.acknowledged}/{row.assigned} acknowledged
                        </li>
                      )) : <li>None. Approved policies are fully acknowledged.</li>}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap gap-2">
                  {isAdmin && !isCreating && selectedPolicy && (
                    <>
                      <button
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
                        onClick={() => void handleApprovalStatus('draft')}
                      >
                        Mark Draft
                      </button>
                      <button
                        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700"
                        onClick={() => void handleApprovalStatus('in_review')}
                      >
                        Send To Review
                      </button>
                      <button
                        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"
                        onClick={() => void handleApprovalStatus('approved')}
                      >
                        Approve
                      </button>
                    </>
                  )}
                  {!isAdmin && !isCreating && selectedPolicy?.status === 'approved' && (
                    selectedPolicyQuery.data?.acknowledgmentSummary.self_acknowledged ? (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
                        Acknowledged on {formatDate(selectedPolicyQuery.data.acknowledgmentSummary.self_acknowledged_at)}
                      </div>
                    ) : (
                      <button
                        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"
                        onClick={() => void handleAcknowledge()}
                      >
                        Acknowledge Policy
                      </button>
                    )
                  )}
                </div>
                {isAdmin ? (
                  <div className="flex gap-2">
                    {isCreating && (
                      <button
                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                        onClick={() => {
                          setIsCreating(false)
                          setDraftSourcePolicyId(null)
                          setDraft(toDraft(selectedPolicy))
                        }}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                      onClick={() => void handleSave()}
                    >
                      <FilePenLine size={16} className="mr-2 inline" />
                      {isCreating ? 'Create Policy' : 'Save Changes'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1 text-sm text-slate-500">
                    <p>You can review policy details here. Editing and approval actions are limited to Admin users.</p>
                    <p>Automated re-acknowledgment notifications and manual export remain deferred to a later policy sprint.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
              Select a policy to review its details.
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
